import i18next from "i18next";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useAllYears, useRiskThemes } from "../api/hooks/useRiskThemes";
import { ControlImplementationEvolution } from "../components/controlImplementationEvolution";
import { IndicatorsFilterList } from "../components/indicatorsFilterList";
import { RiskMatrix } from "../components/riskMatrix";
import { useDesignSystem } from "../context/designSystemContext";
import {
  defaultFilterTypeNumber,
  defaultSelectFilterType,
} from "../types/selectFilter";

export const Indicators = () => {
  useTranslation();
  const [curYear, setCurYear] = useState<defaultFilterTypeNumber>({
    value: new Date().getFullYear(),
    label: new Date().getFullYear(),
  });
  const [priorityFilter, setPriorityFilter] = useState<
    defaultSelectFilterType[]
  >([]);
  const [okrFilter, setOkrFilter] = useState<string[]>(["true", "false"]);
  const [isGlobalGoal, setIsGlobalGoal] = useState<boolean | null>(null);
  const [okrSelectValue, setOkrSelectValue] = useState<string>("1");

  const { data: allYears } = useAllYears();
  const { designSystem } = useDesignSystem();

  const years = allYears?.map((year: number) => {
    return { value: year, label: year };
  });

  const allPriorities = [
    { value: "LOW", label: i18next.t("priority_options_low") },
    { value: "REGULAR", label: i18next.t("priority_options_medium") },
    { value: "HIGH", label: i18next.t("priority_options_high") },
  ];

  const { isLoading, data: allDataRisks } = useRiskThemes(
    curYear.value,
    priorityFilter?.map((option: defaultSelectFilterType) => option.value),
    isGlobalGoal
  );
  // if (error) return <div>Error: {error.message}</div>

  /* ------------------------- funcoes para filtrar os dados ------------------------- */
  const handlePriorityChange = (selectedOptions: any) => {
    setPriorityFilter(selectedOptions || []);
  };
  const handleYearChange = (selectedOptions: any) => {
    setCurYear(selectedOptions);
  };
  const changeOkrFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setOkrSelectValue(value);

    if (value == "1") {
      setOkrFilter(["true", "false"]);
      setIsGlobalGoal(null);
    }
    if (value == "2") {
      setOkrFilter(["true"]);
      setIsGlobalGoal(true);
    }
    if (value == "3") {
      setOkrFilter(["false"]);
      setIsGlobalGoal(false);
    }
  };

  return (
    <div className="h-screen overflow-auto">
      <div className="flex">
        <h1
          className="mx-5 mt-5 font-openSans font-bold text-2xl mb-4 w-1/2"
          style={{ color: designSystem?.cssBasics.primaryColor }}
        >
          {i18next.t("results_dashboard")}
        </h1>

        <div className="w-full flex justify-end mx-1 mb-4 gap-3 w-1/2 mr-4 mt-3">
          {/* okr filter */}
          <div className="flex items-center border border-gray-300 px-4 py-2 rounded-xl shadow-md">
            <p className="mr-2">{i18next.t("okr")}</p>
            <select
              value={okrSelectValue}
              onChange={changeOkrFilter}
              className="border-none focus:ring-0 bg-[#FBFBFF]"
            >
              <option value={"1"}> {i18next.t("all")} </option>
              <option value={"2"}>{i18next.t("yes")}</option>
              <option value={"3"}>{i18next.t("no")}</option>
            </select>
          </div>

          {/* priority filter */}
          <div className="flex items-center border border-gray-300 px-4 py-2 rounded-xl shadow-md">
            <p className="mr-2">{i18next.t("priority")}</p>
            <Select
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              isMulti
              options={allPriorities}
              value={priorityFilter}
              placeholder={i18next.t("select_priority")}
              onChange={handlePriorityChange}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  minHeight: "32px",
                  height: "32px",
                  minWidth: "300px",
                }),
                valueContainer: baseStyles => ({
                  ...baseStyles,
                  height: "32px",
                  padding: "0 7px",
                  display: "flex",
                  justifyContent: "center",
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  margin: "0px",
                  padding: "0px",
                }),
                option: baseStyles => ({
                  ...baseStyles,
                  fontSize: "14px",
                }),
                multiValue: baseStyles => ({
                  ...baseStyles,
                  fontSize: "14px",
                  marginBottom: "7px",
                }),
                multiValueLabel: baseStyles => ({
                  ...baseStyles,
                  textAlign: "center",
                  padding: "0 8px",
                }),
                placeholder: baseStyles => ({
                  ...baseStyles,
                  fontSize: "14px",
                  textAlign: "center",
                  marginBottom: "5px",
                }),
                indicatorSeparator: baseStyles => ({
                  ...baseStyles,
                  marginBottom: "11px",
                }),
              }}
            />
          </div>

          {/* year filter */}
          <div className="flex items-center border border-gray-300 px-4 py-2 rounded-xl shadow-md">
            <p className="mr-2">{i18next.t("year")}</p>
            <Select
              hideSelectedOptions={false}
              closeMenuOnSelect={true}
              options={years}
              value={curYear}
              onChange={handleYearChange}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  minHeight: "32px",
                  height: "32px",
                  minWidth: "90px",
                }),
                valueContainer: baseStyles => ({
                  ...baseStyles,
                  height: "30px",
                  padding: "0 7px",
                  display: "flex",
                  justifyContent: "center",
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  margin: "0px",
                  padding: "0px",
                }),
                option: baseStyles => ({
                  ...baseStyles,
                  fontSize: "14px",
                }),
                singleValue: baseStyles => ({
                  ...baseStyles,
                  fontSize: "14px",
                  marginBottom: "7px",
                }),
                indicatorSeparator: baseStyles => ({
                  ...baseStyles,
                  marginBottom: "11px",
                }),
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 p-4">
        <div className="w-full md:basis-[36%] shadow-lg border border-gray-300 rounded-md">
          <RiskMatrix riskData={allDataRisks} />
        </div>
        <div className="w-full md:basis-[64%] shadow-lg border border-gray-300 rounded-md">
          <ControlImplementationEvolution
            year={curYear.value}
            priority={
              priorityFilter
                ? priorityFilter.map(
                    (option: { value: string; label: string }) => option.value
                  )
                : []
            }
            okr={okrFilter}
          />
        </div>
      </div>

      <hr />
      {isLoading ? (
        <>is loading</> //TODO: colocar tela de carregamento
      ) : (
        <IndicatorsFilterList
          riskData={allDataRisks}
          priorityFilter={
            priorityFilter
              ? priorityFilter.map(
                  (option: { value: string; label: string }) => option.value
                )
              : []
          }
        />
      )}
    </div>
  );
};
